import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  viewChild,
  ViewChild,
} from '@angular/core';
import { FilterAndSortComponent, showRequestErrors } from '@softline/application';
import { Store } from '@softline/core';
import { BehaviorSubject, combineLatestWith, map } from 'rxjs';
import { EmpfaengerAuswahlComponent } from '../../pages/empfaenger-auswahl/empfaenger-auswahl.component';
import {
  FOKAnwender,
  SendNotificationFieldOkStore,
  SOFTLINE_FEATURE_SEND_NOTIFICATION_FIELD_OK,
} from '../../store/empfaenger-field-ok.store';
import {
  EmpfaengerSuchverlaufStore,
  SOFTLINE_FEATURE_ALLGEMEIN_SEND_NOTIFICATION_SUCHVERLAUF,
} from '../../store/empfeanger-suchverlauf.store';
import {
  SendNotificationStore,
  SOFTLINE_FEATURE_SEND_NOTIFICATION,
} from '../../store/send-notification.store';
import { filterConfig } from './default-empfaenger.component.filter';
import { sortConfig } from './default-empfaenger.component.sort';

import { FormatPipe, ToolTipDirective } from '@softline/ui-core';
import { InputComponent } from '@softline/ui-core';
import { RepeatDirective } from '@softline/ui-core';
@Component({
  selector: 'soft-default-empfaenger-auswahl',
  imports: [
    CommonModule,
    FilterAndSortComponent,
    FormatPipe,
    InputComponent,
    RepeatDirective,
    ToolTipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './default-empfaenger-auswahl.component.html',
  styleUrls: ['./default-empfaenger-auswahl.component.css'],
})
export class DefaultEmpfaengerAuswahlComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly hostComponent = inject(EmpfaengerAuswahlComponent);

  private readonly header = viewChild.required<TemplateRef<any>>('header');

  private readonly selectedEmpfaengerIds$ = this.store
    .observe(SOFTLINE_FEATURE_SEND_NOTIFICATION, SendNotificationStore.getters.data)
    .pipe(map((o) => (o?.empfaenger ?? []).map((empfaenger) => empfaenger.id)));

  readonly loading$ = new BehaviorSubject(false);
  readonly input$ = new BehaviorSubject<string | null>(null);

  readonly empfaenger$ = this.store.observe(
    SOFTLINE_FEATURE_SEND_NOTIFICATION_FIELD_OK,
    SendNotificationFieldOkStore.getters.list
  );

  readonly suchverlauf$ = this.store.observe(
    SOFTLINE_FEATURE_ALLGEMEIN_SEND_NOTIFICATION_SUCHVERLAUF,
    EmpfaengerSuchverlaufStore.getters.all
  );

  readonly filteredEmpfaneger$ = this.empfaenger$.pipe(
    combineLatestWith(this.selectedEmpfaengerIds$, this.input$),
    map(([empfaenger, selectedIds, searchTerm]) => {
      const term = searchTerm?.trim().toLowerCase() ?? '';
      return (empfaenger ?? [])
        .filter(
          (e) =>
            !term ||
            e.vorname?.toLowerCase().includes(term) ||
            e.nachname?.toLowerCase().includes(term) ||
            e.kuerzel?.toLowerCase().includes(term)
        )
        .sort((a, b) => a.vorname.localeCompare(b.vorname))
        .map((e) => ({
          ...e,
          selected: selectedIds.includes(e.id),
        }));
    })
  );

  readonly filterConfig = filterConfig;
  readonly sortConfig = sortConfig;

  readonly trackByFn = (index: number, empfaenger: FOKAnwender) => empfaenger.id;

  constructor() {
    //
  }

  async ngOnInit(): Promise<void> {
    this.hostComponent.attachHeader(this.header());
    this.cdRef.detectChanges();
    await this.loadAllEmpfaenger();
  }

  ngOnDestroy(): void {
    this.onEmptyTextfield();
  }

  async loadAllEmpfaenger(): Promise<void> {
    try {
      this.loading$.next(true);

      await this.store.dispatch(
        SOFTLINE_FEATURE_SEND_NOTIFICATION_FIELD_OK,
        SendNotificationFieldOkStore.actions.query,
        {
          params: {
            filter: '  ',
            parameters: {
              kzinternet: 'J,E',
            },
            multiValued: false,
          },
        }
      );
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.loading$.next(false);
    }
  }

  async searchEmpfaenger(searchTerm: string | null): Promise<void> {
    if (!searchTerm || searchTerm?.length < 2) {
      return;
    }

    try {
      this.loading$.next(true);

      this.store.commit(
        SOFTLINE_FEATURE_ALLGEMEIN_SEND_NOTIFICATION_SUCHVERLAUF,
        EmpfaengerSuchverlaufStore.mutations.addOrUpdate,
        {
          date: new Date().toISOString(),
          text: searchTerm,
        }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_SEND_NOTIFICATION_FIELD_OK,
        SendNotificationFieldOkStore.actions.query,
        {
          params: {
            filter: searchTerm ?? '',
            parameters: {
              kzinternet: 'J,E',
            },
            multiValued: false,
          },
        }
      );
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.loading$.next(false);
    }
  }

  onEmptyTextfield(): void {
    this.input$.next(null);

    this.store.commit(
      SOFTLINE_FEATURE_SEND_NOTIFICATION_FIELD_OK,
      SendNotificationFieldOkStore.mutations.reset
    );

    this.store.commit(
      SOFTLINE_FEATURE_SEND_NOTIFICATION_FIELD_OK,
      SendNotificationFieldOkStore.mutations.setList,
      []
    );

    this.store.commit(
      SOFTLINE_FEATURE_SEND_NOTIFICATION_FIELD_OK,
      SendNotificationFieldOkStore.mutations.setValue,
      null
    );
  }

  async selectOrUnselect(anwender: FOKAnwender): Promise<void> {
    const data =
      this.store.get(
        SOFTLINE_FEATURE_SEND_NOTIFICATION,
        SendNotificationStore.getters.data
      ) ?? {};
    let empfaenger = data?.empfaenger || [];
    const selected = empfaenger.findIndex((o) => o.id === anwender.id) > -1;

    if (selected) {
      empfaenger = empfaenger.filter((o) => o.id !== anwender.id);
    } else {
      empfaenger = empfaenger.concat({
        id: anwender.id,
        name: `${anwender.vorname} ${anwender.nachname}`,
      });
    }

    this.store.commit(
      SOFTLINE_FEATURE_SEND_NOTIFICATION,
      SendNotificationStore.mutations.set,
      {
        ...data,
        empfaenger,
      }
    );
  }
}
